import { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../hooks/useAuth';
import {
  financeApi,
  FinanceSummaryResponse,
  RevenueTrendPoint,
  TopGarageResponse,
  OutstandingBalanceResponse,
  SettlementOverviewResponse,
} from '../services/financeApi';

type Period = 'today' | 'week' | 'month' | 'custom';

// Local (not UTC) YYYY-MM-DD so a Dubai admin near midnight doesn't see the wrong day.
function ymd(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function getDateRange(period: Period): { from: string; to: string } {
  const now = new Date();
  const to = ymd(now);
  let from: string;
  switch (period) {
    case 'today':
      from = to;
      break;
    case 'week': {
      // Start of the current week (Monday) → today.
      const d = new Date(now);
      const mondayOffset = (d.getDay() + 6) % 7;
      d.setDate(d.getDate() - mondayOffset);
      from = ymd(d);
      break;
    }
    case 'month':
      // First day of the current calendar month → today (not a rolling 30-day window).
      from = ymd(new Date(now.getFullYear(), now.getMonth(), 1));
      break;
    default:
      from = to;
  }
  return { from, to };
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-AE', { style: 'currency', currency: 'AED', minimumFractionDigits: 2 }).format(value);
}

function InfoNote({ text, infoKey, visibleInfo, setVisibleInfo }: {
  text: string; infoKey: string;
  visibleInfo: string | null; setVisibleInfo: (v: string | null) => void;
}) {
  return (
    <span style={{ position: 'relative', marginLeft: 'auto' }}>
      <button
        onClick={() => setVisibleInfo(visibleInfo === infoKey ? null : infoKey)}
        style={{
          background: 'none', border: '1px solid var(--admin-border)', borderRadius: '50%',
          width: '20px', height: '20px', fontSize: '0.7rem', fontWeight: 700,
          color: 'var(--admin-text-muted)', cursor: 'pointer', display: 'inline-flex',
          alignItems: 'center', justifyContent: 'center', lineHeight: 1,
        }}
        title="How is this calculated?"
      >
        i
      </button>
      {visibleInfo === infoKey && (
        <div style={{
          position: 'absolute', top: '1.75rem', right: 0, zIndex: 10,
          background: 'var(--admin-card-bg, #fff)', border: '1px solid var(--admin-border)',
          borderRadius: '6px', padding: '0.75rem', fontSize: '0.75rem',
          color: 'var(--admin-text-muted)', maxWidth: '280px', lineHeight: 1.5,
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)', whiteSpace: 'normal',
        }}>
          {text}
        </div>
      )}
    </span>
  );
}

/** One stat tile with its "how is this calculated?" note. `value === null` renders the loading dash. */
function StatCard({ infoKey, label, icon, color, info, value, format = formatCurrency, visibleInfo, setVisibleInfo }: {
  infoKey: string; label: string; icon: string; color: string; info: string;
  value: number | null; format?: (n: number) => string;
  visibleInfo: string | null; setVisibleInfo: (v: string | null) => void;
}) {
  return (
    <div className="admin-stat-card" style={{ position: 'relative' }}>
      <div className={`admin-stat-icon ${color}`}>{icon}</div>
      <div style={{ flex: 1 }}>
        <div className="admin-stat-value">{value === null ? '--' : format(value)}</div>
        <div className="admin-stat-label">{label}</div>
      </div>
      <button
        onClick={() => setVisibleInfo(visibleInfo === infoKey ? null : infoKey)}
        style={{
          position: 'absolute', top: '0.5rem', right: '0.5rem',
          background: 'none', border: '1px solid var(--admin-border)', borderRadius: '50%',
          width: '20px', height: '20px', fontSize: '0.7rem', fontWeight: 700,
          color: 'var(--admin-text-muted)', cursor: 'pointer', display: 'flex',
          alignItems: 'center', justifyContent: 'center', lineHeight: 1,
        }}
        title="How is this calculated?"
      >
        i
      </button>
      {visibleInfo === infoKey && (
        <div style={{
          position: 'absolute', top: '2rem', right: '0.5rem', zIndex: 10,
          background: 'var(--admin-card-bg, #fff)', border: '1px solid var(--admin-border)',
          borderRadius: '6px', padding: '0.75rem', fontSize: '0.75rem',
          color: 'var(--admin-text-muted)', maxWidth: '260px', lineHeight: 1.5,
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        }}>
          {info}
        </div>
      )}
    </div>
  );
}

/**
 * #473 — GMV is customer turnover and is labelled as such; Garagat's revenue is its commission.
 * The two never share a label, and the explanations say which column each figure comes from.
 */
const SUMMARY_CARDS = [
  { key: 'gmv' as const, label: 'GMV (customer turnover)', icon: 'AED', color: 'primary', info: 'Gross merchandise value: what customers paid for orders completed in the period, less anything refunded on them. Fully refunded orders are excluded. This is transaction volume, not Garagat income.' },
  { key: 'platformRevenue' as const, label: 'Revenue (commission)', icon: '%', color: 'success', info: 'Garagat\'s own income: the commission snapshotted on those same orders, net of VAT. A customer-fault refund reverses the commission on the refunded slice, so this figure already reflects it.' },
  { key: 'serviceFeeRevenue' as const, label: 'Service fee revenue', icon: 'FEE', color: 'success', info: 'Service fees earned when orders complete, less fees reversed after earning, plus fees recharged to garages. Net of VAT.' },
  { key: 'totalRefunds' as const, label: 'Total Refunds', icon: '-', color: 'danger', info: 'What was refunded on orders completed in the period — the same orders and the same date as GMV, so the three figures reconcile. Includes orders refunded in full.' },
  { key: 'settlementsPaid' as const, label: 'Settlements Paid', icon: 'NET', color: 'info', info: 'Sum of net payable amounts for settlements marked as PAID within the selected period. This is the actual amount transferred to garages.' },
];

/** The VAT the period carries, on the same order basis. Reports → VAT return is the filed figure. */
const VAT_CARDS = [
  { key: 'vatOnCommission' as const, label: 'VAT on commission (output)', info: 'The 5% VAT Garagat charged garages on the commission above. Garagat owes this to the FTA. The month-end return files it by invoice issue date, so a period total here and Box 1 differ by timing.' },
  { key: 'customerVatTotal' as const, label: 'Customer VAT charged (gross of refunds)', info: 'VAT charged to customers on those orders at completion, which VAT-registered garages remit themselves. It is NOT reduced by later refunds — there is no refunded-VAT column on an order — so use Reports → VAT return for anything filed.' },
];

export default function FinanceDashboardPage() {
  const auth = useAuth();
  const [period, setPeriod] = useState<Period>('month');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');

  const [summary, setSummary] = useState<FinanceSummaryResponse | null>(null);
  const [trend, setTrend] = useState<RevenueTrendPoint[]>([]);
  const [topGarages, setTopGarages] = useState<TopGarageResponse[]>([]);
  const [outstanding, setOutstanding] = useState<OutstandingBalanceResponse[]>([]);
  const [settlementOverview, setSettlementOverview] = useState<SettlementOverviewResponse | null>(null);

  const [loadingSummary, setLoadingSummary] = useState(true);
  const [loadingTrend, setLoadingTrend] = useState(true);
  const [loadingGarages, setLoadingGarages] = useState(true);
  const [loadingOutstanding, setLoadingOutstanding] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [visibleInfo, setVisibleInfo] = useState<string | null>(null);

  const fetchData = useCallback(() => {
    if (!auth.token) return;
    const range = period === 'custom' ? { from: customFrom, to: customTo } : getDateRange(period);
    if (!range.from || !range.to) return;

    setError(null);
    setLoadingSummary(true);
    setLoadingTrend(true);
    setLoadingGarages(true);
    setLoadingOutstanding(true);

    financeApi.getSummary(range.from, range.to, auth.token).then(setSummary).catch((err: Error) => setError(err.message)).finally(() => setLoadingSummary(false));
    financeApi.getRevenueTrend(range.from, range.to, auth.token).then(setTrend).catch(() => {}).finally(() => setLoadingTrend(false));
    financeApi.getTopGarages(10, range.from, range.to, auth.token).then(setTopGarages).catch(() => {}).finally(() => setLoadingGarages(false));
    financeApi.getOutstandingBalances(auth.token).then(setOutstanding).catch(() => {}).finally(() => setLoadingOutstanding(false));
    financeApi.getSettlementOverview(auth.token).then(setSettlementOverview).catch(() => {});
  }, [auth.token, period, customFrom, customTo]);

  useEffect(() => { fetchData(); }, [fetchData]);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.75rem', marginBottom: '1.25rem' }}>
        <h2 style={{ fontSize: '1.25rem', fontWeight: 600, color: 'var(--admin-text)', margin: 0 }}>Finance Dashboard</h2>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
          {(['today', 'week', 'month', 'custom'] as Period[]).map((p) => (
            <button
              key={p}
              className={`admin-btn admin-btn-sm ${period === p ? 'admin-btn-primary' : 'admin-btn-outline'}`}
              onClick={() => setPeriod(p)}
            >
              {p === 'today' ? 'Today' : p === 'week' ? 'This Week' : p === 'month' ? 'This Month' : 'Custom'}
            </button>
          ))}
        </div>
      </div>

      {period === 'custom' && (
        <div className="admin-card" style={{ marginBottom: '1rem' }}>
          <div className="admin-toolbar">
            <div className="admin-field">
              <label className="admin-field-label">From</label>
              <input type="date" className="admin-search-input" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} />
            </div>
            <div className="admin-field">
              <label className="admin-field-label">To</label>
              <input type="date" className="admin-search-input" value={customTo} onChange={(e) => setCustomTo(e.target.value)} />
            </div>
            <button className="admin-btn admin-btn-primary admin-btn-sm" disabled={!customFrom || !customTo} onClick={fetchData}>
              Apply
            </button>
          </div>
        </div>
      )}

      {error && <div className="admin-info-banner danger" style={{ marginBottom: '1rem' }}>{error}</div>}

      {/* Summary Stats — GMV and revenue side by side, each with its own label (#473) */}
      <div className="admin-stats-row">
        {/* Spec 068 — the service fee tile renders once the API sends the figure (a pre-068 build omits it). */}
        {SUMMARY_CARDS.filter((card) => card.key !== 'serviceFeeRevenue' || loadingSummary || summary?.serviceFeeRevenue != null).map((card) => (
          <StatCard
            key={card.key}
            infoKey={card.key}
            label={card.label}
            icon={card.icon}
            color={card.color}
            info={card.info}
            value={loadingSummary || !summary ? null : (summary[card.key] ?? null)}
            visibleInfo={visibleInfo}
            setVisibleInfo={setVisibleInfo}
          />
        ))}
      </div>

      <div className="admin-stats-row" style={{ marginTop: '1rem' }}>
        {VAT_CARDS.map((card) => (
          <StatCard
            key={card.key}
            infoKey={card.key}
            label={card.label}
            icon="VAT"
            color="warning"
            info={card.info}
            value={loadingSummary || !summary ? null : summary[card.key]}
            visibleInfo={visibleInfo}
            setVisibleInfo={setVisibleInfo}
          />
        ))}
        <StatCard
          infoKey="totalOrders"
          label="Orders in GMV"
          icon="#"
          color="info"
          info="How many completed orders the GMV and revenue figures above are built from. Fully refunded orders are not counted — they are reported under Total Refunds instead."
          value={loadingSummary || !summary ? null : summary.totalOrders}
          format={(n) => String(n)}
          visibleInfo={visibleInfo}
          setVisibleInfo={setVisibleInfo}
        />
        <StatCard
          infoKey="totalSettlements"
          label="Settlements Raised"
          icon="STL"
          color="primary"
          info="Settlements the cycle created in the period. Voided settlements are excluded: their orders went back to the pool, so they are not an output of the cycle."
          value={loadingSummary || !summary ? null : summary.totalSettlements}
          format={(n) => String(n)}
          visibleInfo={visibleInfo}
          setVisibleInfo={setVisibleInfo}
        />
      </div>

      <div className="admin-two-col" style={{ marginTop: '1rem' }}>
        {/* Revenue Trend */}
        <div className="admin-card" style={{ position: 'relative' }}>
          <div className="admin-card-header">
            <h3 className="admin-card-title">GMV &amp; Revenue Trend</h3>
            <InfoNote text="One row per Asia/Dubai day. The bar is GMV — what customers paid, less refunds — and the green figure beside it is Garagat's revenue for that day: the commission, net of VAT. Fully refunded orders appear in neither." infoKey="trend" visibleInfo={visibleInfo} setVisibleInfo={setVisibleInfo} />
          </div>
          {loadingTrend ? (
            <div className="admin-loading"><div className="admin-loading-spinner"></div></div>
          ) : trend.length === 0 ? (
            <div className="admin-empty-state"><p>No completed orders in this period</p></div>
          ) : (
            <>
              {/* Summary row */}
              <div style={{ display: 'flex', gap: '1.5rem', padding: '0.5rem 0 0.75rem', borderBottom: '1px solid var(--admin-border)', marginBottom: '0.5rem', fontSize: '0.8125rem' }}>
                <div>
                  <span style={{ color: 'var(--admin-text-muted)' }}>GMV: </span>
                  <strong>{formatCurrency(trend.reduce((s, d) => s + d.gmv, 0))}</strong>
                </div>
                <div>
                  <span style={{ color: 'var(--admin-text-muted)' }}>Revenue (commission): </span>
                  <strong style={{ color: 'var(--admin-success, #16a34a)' }}>{formatCurrency(trend.reduce((s, d) => s + d.commission, 0))}</strong>
                </div>
                <div>
                  <span style={{ color: 'var(--admin-text-muted)' }}>Orders: </span>
                  <strong>{trend.reduce((s, d) => s + d.orderCount, 0)}</strong>
                </div>
              </div>
              {/* Daily breakdown with inline bars */}
              <div style={{ maxHeight: '260px', overflowY: 'auto' }}>
                {trend.map((point, i) => {
                  const maxGmv = Math.max(...trend.map((d) => d.gmv), 1);
                  const barPct = (point.gmv / maxGmv) * 100;
                  const dateStr = new Date(point.date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
                  return (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.35rem 0', borderBottom: '1px solid var(--admin-border-light, #f0f0f0)' }}>
                      <div style={{ width: '100px', fontSize: '0.75rem', color: 'var(--admin-text-muted)', flexShrink: 0 }}>{dateStr}</div>
                      <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <div style={{ flex: 1, height: '18px', background: 'var(--admin-bg, #f5f5f5)', borderRadius: '3px', overflow: 'hidden', position: 'relative' }}>
                          <div style={{
                            width: `${Math.max(barPct, 1)}%`, height: '100%',
                            background: 'var(--admin-primary)', borderRadius: '3px',
                            display: 'flex', alignItems: 'center', justifyContent: 'flex-end', paddingRight: '4px',
                          }}>
                            {barPct > 25 && <span style={{ fontSize: '0.625rem', color: '#fff', fontWeight: 600 }}>{formatCurrency(point.gmv)}</span>}
                          </div>
                        </div>
                        {barPct <= 25 && <span style={{ fontSize: '0.6875rem', color: 'var(--admin-text)', fontWeight: 500, flexShrink: 0 }}>{formatCurrency(point.gmv)}</span>}
                      </div>
                      <div style={{ width: '70px', fontSize: '0.6875rem', color: 'var(--admin-success, #16a34a)', textAlign: 'right', flexShrink: 0 }}>{formatCurrency(point.commission)}</div>
                      <div style={{ width: '24px', fontSize: '0.6875rem', color: 'var(--admin-text-muted)', textAlign: 'right', flexShrink: 0 }}>{point.orderCount}</div>
                    </div>
                  );
                })}
              </div>
              {/* Legend */}
              <div style={{ display: 'flex', gap: '1rem', padding: '0.5rem 0 0', fontSize: '0.6875rem', color: 'var(--admin-text-muted)' }}>
                <span><span style={{ display: 'inline-block', width: '8px', height: '8px', background: 'var(--admin-primary)', borderRadius: '2px', marginRight: '4px' }}></span>GMV (customer turnover)</span>
                <span><span style={{ color: 'var(--admin-success, #16a34a)', fontWeight: 600 }}>Green</span> = Revenue (commission)</span>
                <span># = Orders</span>
              </div>
            </>
          )}
        </div>

        {/* Settlement Overview */}
        <div className="admin-card" style={{ position: 'relative' }}>
          <div className="admin-card-header">
            <h3 className="admin-card-title">Settlement Overview</h3>
            <InfoNote text="Total count and value of all settlements grouped by status. Pending = awaiting payment to garage. Paid = completed and transferred." infoKey="overview" visibleInfo={visibleInfo} setVisibleInfo={setVisibleInfo} />
          </div>
          {settlementOverview ? (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '1rem', padding: '0.5rem 0' }}>
              <div className="admin-info-banner warning" style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '0.8125rem' }}>Pending</div>
                <div style={{ fontSize: '1.25rem', fontWeight: 700 }}>{settlementOverview.pendingCount}</div>
                <div style={{ fontSize: '0.75rem', marginTop: '0.25rem' }}>{formatCurrency(settlementOverview.pendingValue)}</div>
              </div>
              <div className="admin-info-banner success" style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '0.8125rem' }}>Paid</div>
                <div style={{ fontSize: '1.25rem', fontWeight: 700 }}>{settlementOverview.paidCount}</div>
                <div style={{ fontSize: '0.75rem', marginTop: '0.25rem' }}>{formatCurrency(settlementOverview.paidValue)}</div>
              </div>
            </div>
          ) : (
            <div className="admin-loading"><div className="admin-loading-spinner"></div></div>
          )}
        </div>
      </div>

      <div className="admin-two-col" style={{ marginTop: '1rem' }}>
        {/* Top Garages */}
        <div className="admin-card" style={{ position: 'relative' }}>
          <div className="admin-card-header">
            <h3 className="admin-card-title">Top Garages by GMV</h3>
            <InfoNote text="Garages ranked by GMV in the selected period — what customers paid them, less refunds. Revenue is Garagat's commission on those orders, net of VAT. Names come from the garages service; a row reads “Unnamed garage” when that lookup was unavailable." infoKey="topGarages" visibleInfo={visibleInfo} setVisibleInfo={setVisibleInfo} />
          </div>
          {loadingGarages ? (
            <div className="admin-loading"><div className="admin-loading-spinner"></div></div>
          ) : topGarages.length === 0 ? (
            <div className="admin-empty-state"><p>No data for this period</p></div>
          ) : (
            <div className="admin-table-wrapper">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Garage</th>
                    <th style={{ textAlign: 'right' }}>GMV</th>
                    <th style={{ textAlign: 'right' }}>Orders</th>
                    <th style={{ textAlign: 'right' }}>Revenue (commission)</th>
                  </tr>
                </thead>
                <tbody>
                  {topGarages.map((g, i) => (
                    <tr key={g.garageId}>
                      <td>{i + 1}</td>
                      <td style={{ fontWeight: 500 }} title={g.garageId}>
                        {g.garageName
                          ? g.garageName
                          : <span style={{ color: 'var(--admin-text-muted)', fontStyle: 'italic', fontWeight: 400 }}>Unnamed garage</span>}
                      </td>
                      <td style={{ textAlign: 'right' }}>{formatCurrency(g.gmv)}</td>
                      <td style={{ textAlign: 'right' }}>{g.orderCount}</td>
                      <td style={{ textAlign: 'right' }}>{formatCurrency(g.commissionEarned)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Outstanding Balances */}
        <div className="admin-card" style={{ position: 'relative' }}>
          <div className="admin-card-header">
            <h3 className="admin-card-title">Outstanding Balances</h3>
            <InfoNote text="Total unpaid amounts owed to each garage from PENDING settlements. Shows net payable (order total minus 20% commission minus 5% VAT on commission, minus refunds)." infoKey="outstanding" visibleInfo={visibleInfo} setVisibleInfo={setVisibleInfo} />
            {outstanding.length > 0 && (
              <span style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--admin-danger)' }}>
                {formatCurrency(outstanding.reduce((sum, d) => sum + d.outstandingAmount, 0))}
              </span>
            )}
          </div>
          {loadingOutstanding ? (
            <div className="admin-loading"><div className="admin-loading-spinner"></div></div>
          ) : outstanding.length === 0 ? (
            <div className="admin-empty-state"><p>No outstanding balances</p></div>
          ) : (
            <div className="admin-table-wrapper">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Garage</th>
                    <th style={{ textAlign: 'right' }}>Outstanding</th>
                    <th style={{ textAlign: 'right' }}>Pending Orders</th>
                    <th>Oldest Unsettled</th>
                  </tr>
                </thead>
                <tbody>
                  {outstanding.map((d) => (
                    <tr key={d.garageId}>
                      <td style={{ fontWeight: 500 }}>{d.garageName || d.garageId.slice(0, 8)}</td>
                      <td style={{ textAlign: 'right', fontWeight: 500, color: 'var(--admin-danger)' }}>
                        {formatCurrency(d.outstandingAmount)}
                      </td>
                      <td style={{ textAlign: 'right' }}>{d.pendingOrderCount}</td>
                      <td>{d.oldestUnsettledDate ? new Date(d.oldestUnsettledDate).toLocaleDateString() : '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
